<script lang="ts">
  import type { BuildSummary } from '../build-state.svelte.js';
  import { i18next } from '../i18n.js';
  import { lang as langState } from '../lang-state.svelte.js';

  interface Props {
    summary: BuildSummary | null;
    verifyValues: Record<string, number>;
    onSet: (stat: string, value: number | null) => void;
    onClearAll: () => void;
  }

  let { summary, verifyValues, onSet, onClearAll }: Props = $props();
  let lang = $derived(langState.current);

  const ADDITIVE_STATS: Array<{ key: string; suffix: string }> = [
    { key: 'wd', suffix: '%' },
    { key: 'chc', suffix: '%' }, { key: 'chd', suffix: '%' }, { key: 'hsd', suffix: '%' },
    { key: 'rof', suffix: '%' }, { key: 'mag', suffix: '%' },
    { key: 'reload', suffix: '%' }, { key: 'handling', suffix: '%' },
    { key: 'dth', suffix: '%' }, { key: 'dta', suffix: '%' }, { key: 'ooc', suffix: '%' },
  ];

  const WEAPON_STATS: Array<{ key: string; suffix: string }> = [
    { key: 'baseDamage', suffix: '' },
    { key: 'magazine', suffix: '' },
    { key: 'rpm', suffix: '' },
    { key: 'reloadSeconds', suffix: 's' },
  ];

  function statName(s: string): string {
    void lang;
    return i18next.t(s, { ns: 'stats', defaultValue: s.toUpperCase() }) as string;
  }

  function additiveValue(key: string): number {
    if (!summary) return 0;
    const a = summary.additive as Record<string, number>;
    return a[key] ?? 0;
  }

  function weaponValue(key: string): number {
    if (!summary?.dpsInput?.weapon) return 0;
    const w = summary.dpsInput.weapon as Record<string, number>;
    return w[key] ?? 0;
  }

  function fmt(n: number, key: string): string {
    if (key === 'baseDamage') return Math.round(n).toLocaleString();
    if (key === 'magazine' || key === 'rpm') return String(Math.round(n));
    if (key === 'reloadSeconds') return n.toFixed(2);
    return n.toFixed(1);
  }

  function setVerify(stat: string, raw: string) {
    if (raw === '') {
      onSet(stat, null);
    } else {
      const n = parseFloat(raw.replace(',', '.'));
      if (Number.isFinite(n)) onSet(stat, n);
    }
  }

  function deltaPct(calc: number, real: number): number {
    if (!Number.isFinite(real) || !Number.isFinite(calc)) return 0;
    if (Math.abs(calc) < 0.001) return real === 0 ? 0 : 100;
    return (real - calc) / Math.abs(calc) * 100;
  }

  function deltaClass(calc: number, real: number | undefined): string {
    if (real === undefined || !Number.isFinite(real)) return '';
    const pct = Math.abs(deltaPct(calc, real));
    if (pct < 0.5) return 'd-ok';
    if (pct < 2) return 'd-warn';
    return 'd-bad';
  }

  function deltaText(calc: number, real: number | undefined): string {
    if (real === undefined || !Number.isFinite(real)) return '';
    const pct = deltaPct(calc, real);
    if (Math.abs(pct) < 0.05) return '≈ 0%';
    return (pct > 0 ? '+' : '') + pct.toFixed(1) + '%';
  }

  let count = $derived(Object.keys(verifyValues).length);
</script>

<section class="panel vfp">
  <div class="panel-title">
    <span>{lang === 'ru' ? 'Сверка с игрой (PRO)' : 'Game verify (PRO)'}</span>
    {#if count > 0}
      <button class="reset" onclick={() => onClearAll()}>{lang === 'ru' ? 'Сбросить' : 'Reset'} ({count})</button>
    {/if}
  </div>

  <div class="hint">
    {lang === 'ru'
      ? 'Введи число из игры — сравним с расчётом. Зелёный = совпадает (<0.5%), жёлтый = небольшое расхождение, красный = >2%. Не влияет на DPS, только сравнение.'
      : 'Type the in-game value — see the delta vs calculator. Green <0.5%, yellow small drift, red >2%. Does not affect DPS — pure comparison.'}
  </div>

  <table class="vf-tbl">
    <thead>
      <tr>
        <th>{lang === 'ru' ? 'Стат' : 'Stat'}</th>
        <th class="num">{lang === 'ru' ? 'Расчёт' : 'Calc'}</th>
        <th class="num">{lang === 'ru' ? 'В игре' : 'In-game'}</th>
        <th class="num">Δ</th>
      </tr>
    </thead>
    <tbody>
      <tr class="group-row"><td colspan="4">{lang === 'ru' ? '— Аддитивные —' : '— Additive —'}</td></tr>
      {#each ADDITIVE_STATS as row (row.key)}
        {@const calc = additiveValue(row.key)}
        {@const real = verifyValues[row.key]}
        {@const klass = deltaClass(calc, real)}
        <tr class={klass}>
          <td class="stat-name">{statName(row.key)}</td>
          <td class="num">{fmt(calc, row.key)}{row.suffix}</td>
          <td class="num">
            <input
              type="number"
              step="0.1"
              class="input vf-in"
              value={real ?? ''}
              placeholder="—"
              oninput={(e) => setVerify(row.key, (e.currentTarget as HTMLInputElement).value)}
            />
          </td>
          <td class="num delta">{deltaText(calc, real)}</td>
        </tr>
      {/each}

      <tr class="group-row"><td colspan="4">{lang === 'ru' ? '— Оружие —' : '— Weapon —'}</td></tr>
      {#each WEAPON_STATS as row (row.key)}
        {@const calc = weaponValue(row.key)}
        {@const real = verifyValues[row.key]}
        {@const klass = deltaClass(calc, real)}
        <tr class={klass}>
          <td class="stat-name">{statName(row.key)}</td>
          <td class="num">{fmt(calc, row.key)}{row.suffix}</td>
          <td class="num">
            <input
              type="number"
              step={row.key === 'reloadSeconds' ? '0.01' : '1'}
              class="input vf-in"
              value={real ?? ''}
              placeholder="—"
              oninput={(e) => setVerify(row.key, (e.currentTarget as HTMLInputElement).value)}
            />
          </td>
          <td class="num delta">{deltaText(calc, real)}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  .vfp { margin-top: 8px; }
  .reset { padding: 4px 10px; font: 700 9px/1 var(--f-display); letter-spacing: .12em; text-transform: uppercase; background: rgba(255,94,98,.1); border: 1px solid rgba(255,94,98,.4); color: var(--red); border-radius: 4px; cursor: pointer; }
  .reset:hover { background: rgba(255,94,98,.2); }
  .hint { font-size: 10.5px; color: var(--muted); margin-bottom: 8px; line-height: 1.45; }
  .vf-tbl { width: 100%; border-collapse: collapse; font-size: 11px; }
  .vf-tbl th { text-align: left; font: 700 9px/1 var(--f-display); letter-spacing: .12em; color: var(--text-dim); padding: 6px 4px; border-bottom: 1px solid var(--border); text-transform: uppercase; }
  .vf-tbl th.num { text-align: right; }
  .vf-tbl td { padding: 4px; border-bottom: 1px solid var(--hair); }
  .vf-tbl td.num { text-align: right; font-family: var(--f-mono); font-variant-numeric: tabular-nums; }
  .stat-name { color: var(--text-dim); font-size: 10.5px; }
  .vf-in { width: 80px; padding: 3px 6px; font-size: 11px; text-align: right; }
  .delta { font-weight: 700; min-width: 60px; }
  .group-row td { font: 700 9px/1 var(--f-display); letter-spacing: .14em; color: var(--orange); padding: 8px 4px 4px; text-transform: uppercase; }
  tr.d-ok .delta { color: var(--green); }
  tr.d-warn .delta { color: var(--yellow); }
  tr.d-bad .delta { color: var(--red); }
  tr.d-bad .vf-in { border-color: rgba(255,94,98,.5); background: rgba(255,94,98,.06); }
  tr.d-warn .vf-in { border-color: rgba(255,216,74,.5); background: rgba(255,216,74,.05); }
  tr.d-ok .vf-in { border-color: rgba(61,166,120,.55); background: rgba(61,166,120,.07); }
</style>
